<template>
    <div class="projet-arbre-de-vie">
        <div class="back-button" @click="$router.push('/projets')">
            <i class="fas fa-arrow-left"></i> Retour
        </div>
        <div class="projet-header">
            <div class="header-content">
                <img src="@/assets/logo_arbredevie.png" alt="Logo L'Arbre de Vie" class="projet-logo">
                <div class="header-text">
                    <h1>Stage - L'Arbre de Vie</h1>
                    <div class="projet-meta">
                        <span class="date">Janvier 2026</span>
                        <span class="category internship">Stage de 2ème année — BTS SIO SISR</span>
                    </div>
                    <div class="competences-badges">
                        <span class="competence-badge">Mettre à disposition des utilisateurs un service informatique</span>
                        <span class="competence-badge">Gérer le patrimoine informatique</span>
                        <span class="competence-badge">Répondre aux incidents et aux demandes d'assistance et d'évolution</span>
                        <span class="competence-badge">Travailler en mode projet</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="projet-content">

            <section class="projet-section">
                <h2>Présentation du stage</h2>
                <p>
                    Dans le cadre de mon stage de deuxième année de <strong>BTS SIO option SISR</strong>, j'ai
                    intégré <strong>L'Arbre de Vie</strong>, une association à but non lucratif basée à
                    <strong>Brest</strong>. L'association disposait d'un réseau informatique sur lequel les
                    utilisateurs étaient régulièrement exposés à des publicités intrusives et des traqueurs.
                </p>
                <p>
                    Ma mission principale était de déployer une solution de <strong>blocage de publicités
                    à l'échelle du réseau</strong>, en agissant au niveau DNS pour filtrer les domaines
                    publicitaires avant même qu'ils n'atteignent les appareils des utilisateurs — sans
                    aucune installation requise sur les machines clientes.
                </p>
            </section>

            <section class="projet-section">
                <h2>Phase 1 : Déploiement de Pi-hole</h2>
                <div class="phase-container">
                    <div class="phase-header">
                        <img src="@/assets/pihole.webp" alt="Logo Pi-hole" class="phase-logo">
                        <div>
                            <h3>Pi-hole</h3>
                            <p class="phase-subtitle">Premier déploiement sur Raspberry Pi</p>
                        </div>
                    </div>
                    <p>
                        La première solution mise en œuvre fut <strong>Pi-hole</strong>, un bloqueur de publicités
                        DNS open source très populaire. Pi-hole s'installe directement sur un serveur Linux et
                        agit comme un résolveur DNS : toute requête vers un domaine référencé dans ses listes de
                        blocage est rejetée, empêchant le chargement des publicités sur l'ensemble du réseau.
                    </p>
                    <div class="steps-grid">
                        <div class="step-card">
                            <div class="step-number">1</div>
                            <p>Installation de <strong>Raspberry Pi OS</strong> sur le Raspberry Pi Zero 2</p>
                        </div>
                        <div class="step-card">
                            <div class="step-number">2</div>
                            <p>Installation de Pi-hole via le script officiel en ligne de commande</p>
                        </div>
                        <div class="step-card">
                            <div class="step-number">3</div>
                            <p>Configuration du serveur DHCP du réseau pour désigner le Raspberry Pi comme résolveur DNS primaire</p>
                        </div>
                        <div class="step-card">
                            <div class="step-number">4</div>
                            <p>Import de listes de blocage supplémentaires et vérification via le tableau de bord web</p>
                        </div>
                    </div>
                    <div class="limitations-box">
                        <h3><i class="fas fa-triangle-exclamation"></i> Limites rencontrées</h3>
                        <ul>
                            <li>Interface d'administration moins intuitive et peu ergonomique</li>
                            <li>Absence de support DNS over HTTPS (DoH) et DNS over TLS (DoT) natif</li>
                            <li>Gestion des règles de filtrage personnalisées plus complexe</li>
                            <li>Mise à jour et maintenance plus lourdes</li>
                            <li>Statistiques moins détaillées et moins lisibles</li>
                            <li>Les appareils mobiles du réseau n'étaient pas filtrés, pour des raisons non identifiées</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Phase 2 : Migration vers AdGuard Home</h2>
                <div class="phase-container">
                    <div class="phase-header">
                        <img src="@/assets/docker.png" alt="Logo Docker" class="phase-logo">
                        <div>
                            <h3>AdGuard Home sur Docker</h3>
                            <p class="phase-subtitle">Solution modernisée et conteneurisée</p>
                        </div>
                    </div>
                    <p>
                        Face aux limitations de Pi-hole, j'ai opté pour une migration vers <strong>AdGuard
                        Home</strong>, une solution plus moderne et plus complète. Le déploiement a été réalisé
                        via <strong>Docker</strong> afin d'assurer une meilleure portabilité, isoler le service
                        du système hôte et faciliter les mises à jour futures.
                    </p>
                    <div class="steps-grid">
                        <div class="step-card">
                            <div class="step-number">1</div>
                            <p>Installation de <strong>Docker Engine</strong> sur le Raspberry Pi Zero 2</p>
                        </div>
                        <div class="step-card">
                            <div class="step-number">2</div>
                            <p>Rédaction d'un fichier <strong>docker-compose.yml</strong> pour AdGuard Home avec mapping des ports et volumes persistants</p>
                        </div>
                        <div class="step-card">
                            <div class="step-number">3</div>
                            <p>Démarrage du conteneur et configuration initiale via l'interface web d'AdGuard Home</p>
                        </div>
                        <div class="step-card">
                            <div class="step-number">4</div>
                            <p>Migration des listes de blocage depuis Pi-hole, tests de fonctionnement et validation avec les utilisateurs</p>
                        </div>
                    </div>
                    <div class="code-block">
                        <div class="code-header">
                            <i class="fab fa-docker"></i> docker-compose.yml (extrait)
                        </div>
                        <pre><code>services:
  adguardhome:
    image: adguard/adguardhome
    container_name: adguardhome
    ports:
      - "53:53/udp"
      - "53:53/tcp"
      - "80:80/tcp"
      - "443:443/tcp"
    volumes:
      - ./work:/opt/adguardhome/work
      - ./conf:/opt/adguardhome/conf
    restart: unless-stopped</code></pre>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Architecture déployée</h2>
                <div class="architecture-diagram">
                    <div class="arch-grid">
                        <div class="arch-node clients">
                            <i class="fas fa-laptop"></i>
                            <span>Appareils clients</span>
                            <small>PC, smartphones, tablettes</small>
                        </div>
                        <div class="arch-harrow arch-harrow-1">
                            <i class="fas fa-arrow-right"></i>
                            <small>Requête DNS</small>
                        </div>
                        <div class="arch-node router">
                            <i class="fas fa-network-wired"></i>
                            <span>Routeur réseau</span>
                            <small>DNS → Pi Zero 2</small>
                        </div>
                        <div class="arch-harrow arch-harrow-2">
                            <i class="fas fa-arrow-right"></i>
                            <small>Transfert DNS</small>
                        </div>
                        <div class="arch-node rpi">
                            <i class="fas fa-server"></i>
                            <span>Raspberry Pi Zero 2</span>
                            <div class="docker-container-badge">
                                <span class="docker-label"><i class="fab fa-docker"></i> Docker</span>
                                <span class="adguard-label"><i class="fas fa-shield-halved"></i> AdGuard Home</span>
                            </div>
                        </div>
                        <div class="arch-varrow">
                            <div class="varrow-inner">
                                <div class="varrow-line"></div>
                                <div class="varrow-labels">
                                    <span class="blocked-label"><i class="fas fa-ban"></i> Domaine bloqué → réponse vide</span>
                                    <span class="allowed-label"><i class="fas fa-check"></i> Domaine légitime → résolution normale</span>
                                </div>
                            </div>
                        </div>
                        <div class="arch-node dns">
                            <i class="fas fa-globe"></i>
                            <span>Résolveur DNS externe</span>
                            <small>Cloudflare 1.1.1.1</small>
                        </div>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Pi-hole vs AdGuard Home</h2>
                <div class="comparison-grid">
                    <div class="comparison-card pihole-card">
                        <div class="comparison-header">
                            <i class="fas fa-circle-dot"></i>
                            <h3>Pi-hole</h3>
                        </div>
                        <ul>
                            <li class="pro">Installation simple et rapide</li>
                            <li class="pro">Large communauté et documentation abondante</li>
                            <li class="pro">Très répandu, testé en production</li>
                            <li class="con">Interface peu ergonomique</li>
                            <li class="con">Pas de support DoH/DoT natif</li>
                            <li class="con">Règles personnalisées complexes à gérer</li>
                            <li class="con">Maintenance et mises à jour plus lourdes</li>
                        </ul>
                    </div>
                    <div class="comparison-card adguard-card">
                        <div class="comparison-header">
                            <i class="fas fa-shield-halved"></i>
                            <h3>AdGuard Home</h3>
                        </div>
                        <ul>
                            <li class="pro">Interface moderne et intuitive</li>
                            <li class="pro">Support DNS over HTTPS et DNS over TLS</li>
                            <li class="pro">Règles de filtrage avancées et flexibles</li>
                            <li class="pro">Déployable via Docker (isolation, portabilité)</li>
                            <li class="pro">Statistiques détaillées par client</li>
                            <li class="pro">Mises à jour fréquentes et actives</li>
                            <li class="con">Moins documenté que Pi-hole</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Compétences développées</h2>
                <div class="skills-grid">
                    <div class="skill-item">
                        <h3><i class="fas fa-server"></i> Administration Linux</h3>
                        <p>Configuration d'un serveur Raspberry Pi OS, gestion des services système, des permissions et des interfaces réseau</p>
                    </div>
                    <div class="skill-item">
                        <h3><i class="fab fa-docker"></i> Docker & Conteneurisation</h3>
                        <p>Déploiement d'applications via Docker et Docker Compose, gestion des volumes persistants et du mapping de ports</p>
                    </div>
                    <div class="skill-item">
                        <h3><i class="fas fa-network-wired"></i> Réseau & DNS</h3>
                        <p>Compréhension approfondie du protocole DNS, configuration de résolveurs, filtrage de domaines et règles personnalisées</p>
                    </div>
                    <div class="skill-item">
                        <h3><i class="fas fa-shield-halved"></i> Cybersécurité</h3>
                        <p>Mise en place de protections contre les publicités, les traqueurs et les domaines malveillants à l'échelle du réseau</p>
                    </div>
                    <div class="skill-item">
                        <h3><i class="fas fa-arrows-rotate"></i> Migration de services</h3>
                        <p>Analyse des limites d'une solution existante, planification et exécution d'une migration sans interruption de service</p>
                    </div>
                </div>
            </section>

            <section class="projet-section">
                <h2>Conclusion</h2>
                <p>
                    Ce stage m'a permis de mettre en pratique des compétences concrètes en administration
                    système, réseau et conteneurisation dans un contexte professionnel réel. Le déploiement
                    d'AdGuard Home via Docker sur un Raspberry Pi Zero 2 a considérablement amélioré la qualité
                    de navigation des utilisateurs de l'association L'Arbre de Vie en bloquant les
                    publicités et les traqueurs sur l'ensemble du réseau, sans aucune intervention sur les
                    postes clients.
                </p>
                <p>
                    Cette expérience m'a également appris à évaluer une solution existante, à identifier
                    ses limites et à proposer une alternative plus adaptée — une compétence essentielle
                    dans les métiers de l'infrastructure IT.
                </p>
            </section>

        </div>
    </div>
</template>

<script>
export default {
    name: 'ProjetArbreDeVie'
}
</script>

<style scoped>
.projet-arbre-de-vie {
    padding: 2rem;
    min-height: 100vh;
    max-width: 1200px;
    margin: 0 auto;
    color: #e0e0e0;
}

.back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #4CAF50;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    cursor: pointer;
    transition: color 0.3s;
}

.back-button:hover {
    color: #81C784;
}

.projet-header {
    margin-bottom: 3rem;
    border-bottom: 2px solid #4CAF50;
    padding-bottom: 1.5rem;
}

.header-content {
    display: flex;
    align-items: center;
    gap: 2rem;
}

.projet-logo {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #4CAF50;
    flex-shrink: 0;
}

.phase-logo {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(76, 175, 80, 0.4);
    flex-shrink: 0;
}

.header-text {
    flex: 1;
}

h1 {
    color: #4CAF50;
    font-size: 2.8rem;
    margin-bottom: 1rem;
}

.projet-meta {
    display: flex;
    gap: 1.5rem;
    font-size: 1.1rem;
    flex-wrap: wrap;
    align-items: center;
}

.date {
    color: #81C784;
}

.category {
    padding: 0.3rem 1rem;
    border-radius: 20px;
    font-weight: 500;
}

.category.internship {
    background-color: rgba(76, 175, 80, 0.1);
    color: #4CAF50;
    border: 1px solid #4CAF50;
}

.projet-section {
    margin-bottom: 3rem;
}

h2 {
    color: #81C784;
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
    border-left: 4px solid #4CAF50;
    padding-left: 1rem;
}

h3 {
    color: #A5D6A7;
    font-size: 1.3rem;
    margin-bottom: 0.8rem;
}

p {
    line-height: 1.7;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    color: #B0BEC5;
}

strong {
    color: #e0e0e0;
}

/* Phase containers */
.phase-container {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #4CAF50;
    border-radius: 8px;
    padding: 1.5rem;
}

.phase-header {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    margin-bottom: 1.2rem;
}

.phase-icon {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    flex-shrink: 0;
}

.pihole-icon {
    background: rgba(227, 70, 70, 0.15);
    border: 2px solid #E34646;
    color: #E34646;
}

.adguard-icon {
    background: rgba(76, 175, 80, 0.15);
    border: 2px solid #4CAF50;
    color: #4CAF50;
}

.phase-subtitle {
    color: #90A4AE;
    font-size: 0.95rem;
    margin: 0;
    font-style: italic;
}

/* Steps grid */
.steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin: 1.5rem 0;
}

.step-card {
    background: rgba(76, 175, 80, 0.05);
    border: 1px solid rgba(76, 175, 80, 0.3);
    border-radius: 8px;
    padding: 1rem;
    display: flex;
    gap: 1rem;
    align-items: flex-start;
}

.step-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #4CAF50;
    color: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.9rem;
    flex-shrink: 0;
}

.step-card p {
    margin: 0;
    font-size: 0.95rem;
    color: #B0BEC5;
}

/* Limitations box */
.limitations-box {
    background: rgba(227, 70, 70, 0.07);
    border: 1px solid rgba(227, 70, 70, 0.4);
    border-radius: 8px;
    padding: 1.2rem 1.5rem;
    margin-top: 1rem;
}

.limitations-box h3 {
    color: #EF9A9A;
    margin-bottom: 0.8rem;
    font-size: 1.1rem;
}

.limitations-box ul {
    padding-left: 1.5rem;
    margin: 0;
}

.limitations-box li {
    color: #B0BEC5;
    margin-bottom: 0.5rem;
    line-height: 1.5;
}

/* Code block */
.code-block {
    background: #0d1117;
    border: 1px solid rgba(76, 175, 80, 0.3);
    border-radius: 8px;
    overflow: hidden;
    margin-top: 1.5rem;
}

.code-header {
    background: rgba(76, 175, 80, 0.1);
    padding: 0.6rem 1rem;
    color: #81C784;
    font-size: 0.9rem;
    border-bottom: 1px solid rgba(76, 175, 80, 0.3);
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.code-block pre {
    margin: 0;
    padding: 1.2rem;
    overflow-x: auto;
}

.code-block code {
    color: #A5D6A7;
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    line-height: 1.6;
}

/* Architecture diagram */
.architecture-diagram {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #4CAF50;
    border-radius: 8px;
    padding: 2rem;
    margin: 2rem 0;
}

.arch-grid {
    display: grid;
    grid-template-columns: 1fr 70px 1fr 70px 1fr;
    grid-template-rows: auto auto auto;
    grid-template-areas:
        "clients h1 router h2 rpi"
        ".       .  .      .  varrow"
        ".       .  .      .  dns";
    align-items: center;
    gap: 1rem 0;
}

.arch-node.clients  { grid-area: clients; }
.arch-harrow-1      { grid-area: h1; }
.arch-node.router   { grid-area: router; }
.arch-harrow-2      { grid-area: h2; }
.arch-node.rpi      { grid-area: rpi; }
.arch-varrow        { grid-area: varrow; }
.arch-node.dns      { grid-area: dns; }

.arch-node {
    background: rgba(76, 175, 80, 0.08);
    border: 2px solid #4CAF50;
    border-radius: 10px;
    padding: 1rem 1.2rem;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
}

.arch-node i {
    font-size: 2rem;
    color: #4CAF50;
}

.arch-node span {
    font-weight: 600;
    color: #e0e0e0;
    font-size: 0.95rem;
}

.arch-node small {
    color: #90A4AE;
    font-size: 0.78rem;
}

.arch-harrow {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #4CAF50;
    font-size: 1.2rem;
    gap: 0.2rem;
}

.arch-harrow small {
    color: #81C784;
    font-size: 0.72rem;
    white-space: nowrap;
}

.docker-container-badge {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin-top: 0.4rem;
    width: 100%;
}

.docker-label {
    background: rgba(0, 150, 255, 0.15);
    border: 1px solid rgba(0, 150, 255, 0.4);
    border-radius: 4px;
    padding: 0.2rem 0.5rem;
    font-size: 0.75rem;
    color: #90CAF9;
}

.adguard-label {
    background: rgba(76, 175, 80, 0.15);
    border: 1px solid rgba(76, 175, 80, 0.5);
    border-radius: 4px;
    padding: 0.2rem 0.5rem;
    font-size: 0.75rem;
    color: #A5D6A7;
}

.arch-varrow {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0;
}

.varrow-inner {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.varrow-line {
    width: 2px;
    height: 50px;
    background: #4CAF50;
    position: relative;
    flex-shrink: 0;
}

.varrow-line::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: -4px;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 8px solid #4CAF50;
}

.varrow-labels {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.blocked-label {
    color: #EF9A9A;
    font-size: 0.85rem;
}

.allowed-label {
    color: #A5D6A7;
    font-size: 0.85rem;
}

/* Comparison */
.comparison-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2rem;
    margin: 1.5rem 0;
}

.comparison-card {
    background: rgba(1, 1, 11, 0.95);
    border-radius: 8px;
    padding: 1.5rem;
}

.pihole-card {
    border: 1px solid #E34646;
}

.adguard-card {
    border: 1px solid #4CAF50;
}

.comparison-header {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 1.2rem;
}

.pihole-card .comparison-header i,
.pihole-card .comparison-header h3 {
    color: #EF9A9A;
}

.adguard-card .comparison-header i,
.adguard-card .comparison-header h3 {
    color: #81C784;
}

.comparison-card h3 {
    margin: 0;
}

.comparison-card ul {
    list-style: none;
    padding: 0;
    margin: 0;
}

.comparison-card li {
    padding: 0.4rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    font-size: 0.95rem;
    color: #B0BEC5;
}

.comparison-card li:last-child {
    border-bottom: none;
}

.comparison-card li.pro::before {
    content: '✓ ';
    color: #4CAF50;
    font-weight: bold;
}

.comparison-card li.con::before {
    content: '✗ ';
    color: #EF9A9A;
    font-weight: bold;
}

/* Skills */
.skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin: 2rem 0;
}

.skill-item {
    background: rgba(1, 1, 11, 0.95);
    border: 1px solid #4CAF50;
    border-radius: 8px;
    padding: 1.5rem;
}

.skill-item h3 {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #A5D6A7;
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
}

.skill-item h3 i {
    color: #4CAF50;
}

.skill-item p {
    margin: 0;
    font-size: 1rem;
}

.competences-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1rem;
}

.competence-badge {
    font-size: 0.82rem;
    font-style: italic;
    padding: 0.2rem 0.7rem;
    border-radius: 4px;
    border-left: 2px solid rgba(76, 175, 80, 0.5);
    color: #A5D6A7;
    background: rgba(76, 175, 80, 0.1);
}

@media screen and (max-width: 768px) {
    .header-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .projet-logo {
        width: 60px;
        height: 60px;
    }

    h1 {
        font-size: 2rem;
    }

    .arch-grid {
        grid-template-columns: 1fr;
        grid-template-areas:
            "clients"
            "h1"
            "router"
            "h2"
            "rpi"
            "varrow"
            "dns";
    }

    .arch-harrow {
        transform: rotate(90deg);
    }

    .skills-grid,
    .comparison-grid {
        grid-template-columns: 1fr;
    }

    .steps-grid {
        grid-template-columns: 1fr;
    }
}
</style>
